import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import {
  Users, Calendar, ClipboardCheck, Award, Clock, CheckCircle, XCircle, FileText, Video,
  IndianRupee, FileQuestion, CalendarClock, TrendingUp, AlertCircle, Download,
  Eye, MessageSquare, Plus
} from 'lucide-react';
import MessagesTab from '@/components/MessagesTab';
import { VivaTab, OsceTab, QuestionBankTab } from '@/components/examiner';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, color, subtext }) {
  return (
    <Card className="imcac-card">
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-[#1B2A4A] font-heading">{value}</p>
          <p className="text-xs text-slate-500">{label}</p>
          {subtext && <p className="text-xs text-slate-400">{subtext}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function ExaminerDashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  // Existing state
  const [candidates, setCandidates] = useState([]);
  const [allocatedCandidates, setAllocatedCandidates] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [vivaSchedules, setVivaSchedules] = useState([]);
  const [caseLogs, setCaseLogs] = useState([]);
  const [submittedLogbooks, setSubmittedLogbooks] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [selectedCaseLog, setSelectedCaseLog] = useState(null);
  const [selectedLogbook, setSelectedLogbook] = useState(null);
  
  // New state for Phase 2 features
  const [dashboardData, setDashboardData] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [questionBank, setQuestionBank] = useState([]);
  const [osceScenarios, setOsceScenarios] = useState([]);
  const [rescheduleRequests, setRescheduleRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('candidates');
  
  // Transcript state
  const [transcriptCandidates, setTranscriptCandidates] = useState([]);
  const [submittedTranscripts, setSubmittedTranscripts] = useState([]);
  const [selectedTranscriptCandidate, setSelectedTranscriptCandidate] = useState(null);
  const [showTranscriptForm, setShowTranscriptForm] = useState(false);
  const [transcriptForm, setTranscriptForm] = useState({
    subject_scores: {},
    practical_score: 0,
    osce_score: 0,
    viva_score: 0,
    overall_remarks: '',
    documents: []
  });
  
  // Forms state
  const [gradeForm, setGradeForm] = useState({ grade: '', feedback: '' });
  const [caseScoreForm, setCaseScoreForm] = useState({
    clinical_reasoning_score: 7,
    technique_score: 7,
    documentation_score: 7,
    patient_safety_score: 7,
    overall_score: 7,
    feedback: '',
    examiner_notes: ''
  });
  const [showRescheduleForm, setShowRescheduleForm] = useState(false);
  const [rescheduleForm, setRescheduleForm] = useState({
    request_type: '', schedule_id: '', reason: '', preferred_dates: [], additional_notes: ''
  });

  // Reschedule handler
  useEffect(() => {
    if (token) {
      fetchAll();
    }
  }, [token]);// eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    fetchCandidates();
    fetchAllocatedCandidates();
    fetchSchedules();
    fetchCaseLogs();
    fetchSubmittedLogbooks();
    fetchVivaSchedules();
    fetchDashboard();
    fetchEarnings();
    fetchQuestionBank();
    fetchOsceScenarios();
    fetchRescheduleRequests();
    fetchTranscriptCandidates();
    fetchSubmittedTranscripts();
  };

  const fetchSubmittedLogbooks = async () => {
    try {
      const res = await axios.get(`${API}/examiner/submitted-logbooks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmittedLogbooks(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchSubmittedTranscripts = async () => {
    try {
      const res = await axios.get(`${API}/examiner/my-transcripts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmittedTranscripts(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchTranscriptCandidates = async () => {
    try {
      // Get candidates who have completed exams and are ready for transcript
      const res = await axios.get(`${API}/examiner/allocated-candidates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to candidates with completed or scheduled status
      // Status can be at c.status or c.schedule.status depending on the data structure
      const transcriptReady = res.data.filter(c => {
        const status = c.status || c.schedule?.status;
        return ['scheduled', 'rescheduled', 'completed'].includes(status);
      });
      // Map to expected format for UI
      const mappedCandidates = transcriptReady.map(c => ({
        id: c.application_id || c.id,
        user_id: c.candidate?.id || c.user_id,
        full_name: c.candidate?.name || c.full_name,
        email: c.candidate?.email || c.email,
        examination: c.examination?.name || c.examination?.type || c.examination,
        status: c.status || c.schedule?.status,
        transcript_status: c.transcript_status
      }));
      setTranscriptCandidates(mappedCandidates);
    } catch (err) { console.error(err); }
  };

  const fetchCandidates = async () => {
    try {
      const res = await axios.get(`${API}/examiner/assigned-candidates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCandidates(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchAllocatedCandidates = async () => {
    try {
      const res = await axios.get(`${API}/examiner/allocated-candidates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllocatedCandidates(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchSchedules = async () => {
    try {
      const res = await axios.get(`${API}/examiner/osce-schedules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchedules(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchCaseLogs = async () => {
    try {
      const res = await axios.get(`${API}/examiner/case-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCaseLogs(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchVivaSchedules = async () => {
    try {
      const res = await axios.get(`${API}/examiner/my-viva-schedules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVivaSchedules(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/examiner/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchEarnings = async () => {
    try {
      const res = await axios.get(`${API}/examiner/earnings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEarnings(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchQuestionBank = async () => {
    try {
      const res = await axios.get(`${API}/examiner/question-bank`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestionBank(res.data.questions || []);
    } catch (err) { console.error(err); }
  };

  const fetchOsceScenarios = async () => {
    try {
      const res = await axios.get(`${API}/examiner/osce-scenarios`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOsceScenarios(res.data.scenarios || []);
    } catch (err) { console.error(err); }
  };

  const fetchRescheduleRequests = async () => {
    try {
      const res = await axios.get(`${API}/examiner/reschedule-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRescheduleRequests(res.data);
    } catch (err) { console.error(err); }
  };

  const handleConfirmViva = async (scheduleId) => {
    try {
      await axios.put(`${API}/examiner/viva-schedules/${scheduleId}/confirm`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Viva availability confirmed');
      fetchVivaSchedules();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to confirm');
    }
  };

  const handleGrade = async (appId) => {
    if (!gradeForm.grade) { toast.error('Please select a grade'); return; }
    try {
      await axios.post(`${API}/examiner/grade-candidate?app_id=${appId}&grade=${gradeForm.grade}&feedback=${encodeURIComponent(gradeForm.feedback)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Candidate graded successfully');
      setSelectedCandidate(null);
      setGradeForm({ grade: '', feedback: '' });
      fetchCandidates();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to grade candidate'); }
  };

  const handleScoreCaseLog = async () => {
    try {
      await axios.post(`${API}/examiner/case-logs/${selectedCaseLog.id}/score`, {
        case_log_id: selectedCaseLog.id,
        ...caseScoreForm
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Case log scored successfully');
      setSelectedCaseLog(null);
      setCaseScoreForm({
        clinical_reasoning_score: 7,
        technique_score: 7,
        documentation_score: 7,
        patient_safety_score: 7,
        overall_score: 7,
        feedback: '',
        examiner_notes: ''
      });
      fetchCaseLogs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to score case log'); }
  };

  const handleSubmitRescheduleRequest = async () => {
    if (!rescheduleForm.request_type || !rescheduleForm.schedule_id || !rescheduleForm.reason) {
      toast.error('Please fill required fields');
      return;
    }
    try {
      await axios.post(`${API}/examiner/reschedule-requests`, rescheduleForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Reschedule request submitted');
      setShowRescheduleForm(false);
      setRescheduleForm({ request_type: '', schedule_id: '', reason: '', preferred_dates: [], additional_notes: '' });
      fetchRescheduleRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit request'); }
  };

  const handleSubmitTranscript = async () => {
    if (!selectedTranscriptCandidate) {
      toast.error('Please select a candidate');
      return;
    }
    try {
      const res = await axios.post(`${API}/examiner/submit-transcript`, {
        candidate_id: selectedTranscriptCandidate.user_id,
        application_id: selectedTranscriptCandidate.id,
        subject_scores: transcriptForm.subject_scores,
        practical_score: transcriptForm.practical_score,
        osce_score: transcriptForm.osce_score,
        viva_score: transcriptForm.viva_score,
        overall_remarks: transcriptForm.overall_remarks
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.is_passed && res.data.certificate_pending) {
        toast.success('Transcript submitted! Student PASSED - Certificate also sent for admin approval.');
      } else if (res.data.is_passed) {
        toast.success('Transcript submitted for admin verification. Student PASSED!');
      } else {
        toast.success('Transcript submitted for admin verification.');
      }
      
      setShowTranscriptForm(false);
      setSelectedTranscriptCandidate(null);
      setTranscriptForm({ subject_scores: {}, practical_score: 0, osce_score: 0, viva_score: 0, overall_remarks: '', documents: [] });
      fetchTranscriptCandidates();
      fetchSubmittedTranscripts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit transcript'); }
  };

  if (user?.role !== 'examiner' && user?.role !== 'admin') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="examiner-access-denied">
        <Card className="imcac-card max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <XCircle className="h-12 w-12 text-red-400 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-[#1B2A4A] font-heading mb-2">Access Denied</h2>
            <p className="text-sm text-slate-500">This page is restricted to IMCAC examiners.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pendingGrading = candidates.filter(c => c.status === 'exam_scheduled' || c.status === 'approved');
  const gradedCandidates = candidates.filter(c => c.status === 'graded');
  const pendingCaseLogs = caseLogs.filter(l => l.status === 'assigned');

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="examiner-dashboard">
      {/* Header */}
      <section className="bg-[#1B2A4A] py-10">
        <div className="imcac-container">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-[#C5A572] mb-2">Examiner Portal</p>
              <h1 className="text-3xl font-bold text-white tracking-tight font-heading">Welcome, {user?.full_name}</h1>
              <p className="text-slate-300 text-sm mt-1">
                {dashboardData?.examiner?.examiner_id && (
                  <span className="bg-[#C5A572]/20 px-2 py-0.5 rounded mr-2">{dashboardData.examiner.examiner_id}</span>
                )}
                Manage assessments, vivas, case logs, and examination content
              </p>
            </div>
            <div className="flex gap-2">
              {dashboardData?.calibration?.status !== 'calibrated' && (
                <Badge className="bg-amber-100 text-amber-700 animate-pulse">
                  <AlertCircle className="h-3 w-3 mr-1" /> Calibration {dashboardData?.calibration?.status || 'Pending'}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="imcac-container py-8">
        {/* Stats - Enhanced */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8" data-testid="examiner-stats">
          <StatCard icon={Users} label="Assigned Candidates" value={dashboardData?.stats?.assigned_candidates || candidates.length} color="bg-blue-100 text-blue-600" />
          <StatCard icon={Clock} label="Pending Grading" value={pendingGrading.length} color="bg-amber-100 text-amber-600" />
          <StatCard icon={ClipboardCheck} label="Pending Case Logs" value={dashboardData?.stats?.pending_case_logs || pendingCaseLogs.length} color="bg-purple-100 text-purple-600" />
          <StatCard icon={Video} label="Upcoming Vivas" value={dashboardData?.stats?.pending_vivas || vivaSchedules.filter(v => v.status !== 'completed').length} color="bg-indigo-100 text-indigo-600" />
          <StatCard icon={CheckCircle} label="Completed" value={dashboardData?.stats?.completed_assessments || gradedCandidates.length} color="bg-green-100 text-green-600" />
          <StatCard 
            icon={IndianRupee} 
            label="Earnings (10%)" 
            value={earnings ? `₹${(earnings.total_earnings || 0).toLocaleString()}` : '...'} 
            color="bg-emerald-100 text-emerald-600" 
            subtext={earnings ? `${earnings.total_assessments} assessments` : ''}
          />
        </div>

        {/* Main Tabs */}
        <Card className="imcac-card">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full justify-start h-auto flex-wrap bg-[#F8FAFC] rounded-none border-b border-slate-200 p-1 gap-1">
              <TabsTrigger value="candidates" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
                <Users className="h-4 w-4 mr-1" /> Candidates
              </TabsTrigger>
              <TabsTrigger value="caselogs" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
                <ClipboardCheck className="h-4 w-4 mr-1" /> Case Logs
              </TabsTrigger>
              <TabsTrigger value="viva" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white" data-testid="tab-viva">
                <Video className="h-4 w-4 mr-1" /> My Vivas ({vivaSchedules.length})
              </TabsTrigger>
              <TabsTrigger value="schedules" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
                <Calendar className="h-4 w-4 mr-1" /> OSCE Schedules
              </TabsTrigger>
              <TabsTrigger value="questions" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white" data-testid="tab-questions">
                <FileQuestion className="h-4 w-4 mr-1" /> Question Bank
              </TabsTrigger>
              <TabsTrigger value="earnings" className="rounded-sm text-sm data-[state=active]:bg-[#C5A572] data-[state=active]:text-white" data-testid="tab-earnings">
                <IndianRupee className="h-4 w-4 mr-1" /> Earnings
              </TabsTrigger>
              <TabsTrigger value="requests" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white" data-testid="tab-requests">
                <CalendarClock className="h-4 w-4 mr-1" /> Requests
              </TabsTrigger>
              <TabsTrigger value="transcripts" className="rounded-sm text-sm data-[state=active]:bg-[#C5A572] data-[state=active]:text-white" data-testid="tab-transcripts">
                <FileText className="h-4 w-4 mr-1" /> Transcripts
              </TabsTrigger>
              <TabsTrigger value="messages" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white" data-testid="tab-messages">
                <MessageSquare className="h-4 w-4 mr-1" /> Messages
              </TabsTrigger>
            </TabsList>

            {/* Candidates Tab */}
            <TabsContent value="candidates" className="p-4">
              {/* Allocated Candidates Section - New examination allocations */}
              {allocatedCandidates.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-base font-semibold text-[#1B2A4A] mb-3 flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-green-600" />
                    Scheduled Examinations
                    <Badge className="bg-green-100 text-green-700">{allocatedCandidates.length}</Badge>
                  </h3>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-green-50">
                          <TableHead className="text-xs font-bold">Candidate</TableHead>
                          <TableHead className="text-xs font-bold">Exam Details</TableHead>
                          <TableHead className="text-xs font-bold">Schedule</TableHead>
                          <TableHead className="text-xs font-bold">Center</TableHead>
                          <TableHead className="text-xs font-bold">Status</TableHead>
                          <TableHead className="text-xs font-bold">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {allocatedCandidates.map((alloc) => (
                          <TableRow key={alloc.allocation_id} className="bg-green-50/50">
                            <TableCell>
                              <p className="text-sm font-medium text-[#1B2A4A]">{alloc.candidate?.name}</p>
                              <p className="text-xs text-slate-500">{alloc.candidate?.email}</p>
                              <p className="text-xs text-slate-400">{alloc.candidate?.phone}</p>
                            </TableCell>
                            <TableCell>
                              <Badge className="text-xs rounded-sm bg-purple-100 text-purple-700">{alloc.examination?.type}</Badge>
                              <p className="text-xs text-slate-600 mt-1">{alloc.examination?.name}</p>
                              <p className="text-xs text-slate-400">Route {alloc.examination?.route}</p>
                            </TableCell>
                            <TableCell>
                              <p className="text-sm font-medium text-[#1B2A4A]">{alloc.schedule?.date}</p>
                              <p className="text-xs text-slate-500">{alloc.schedule?.time || 'Time TBD'}</p>
                            </TableCell>
                            <TableCell>
                              <p className="text-xs text-slate-600">{alloc.examination_center?.name}</p>
                              <p className="text-xs text-slate-400 line-clamp-1">{alloc.examination_center?.address}</p>
                            </TableCell>
                            <TableCell>
                              <Badge className={`text-xs rounded-sm ${
                                alloc.schedule?.status === 'scheduled' ? 'bg-green-100 text-green-700' :
                                alloc.schedule?.status === 'rescheduled' ? 'bg-amber-100 text-amber-700' :
                                'bg-slate-100 text-slate-700'
                              }`}>
                                {alloc.schedule?.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Button
                                size="sm"
                                className="h-7 px-2 text-xs rounded-sm bg-[#C5A572] text-white"
                                onClick={() => navigate(`/examiner/assess/${alloc.candidate?.id}`)}
                                data-testid={`assess-allocated-${alloc.allocation_id}`}
                              >
                                <FileText className="h-3 w-3 mr-1" /> View Profile
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Existing Assigned Candidates */}
              <h3 className="text-base font-semibold text-[#1B2A4A] mb-3">All Assigned Candidates</h3>
              {candidates.length === 0 ? (
                <p className="text-sm text-slate-500 py-8 text-center">No candidates assigned yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F8FAFC]">
                        <TableHead className="text-xs font-bold">Name</TableHead>
                        <TableHead className="text-xs font-bold">Examination</TableHead>
                        <TableHead className="text-xs font-bold">Route</TableHead>
                        <TableHead className="text-xs font-bold">Status</TableHead>
                        <TableHead className="text-xs font-bold">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {candidates.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell className="text-sm font-medium text-[#1B2A4A]">{c.full_name}</TableCell>
                          <TableCell className="text-xs text-slate-600">{c.examination}</TableCell>
                          <TableCell className="text-xs text-slate-500">Route {c.route}</TableCell>
                          <TableCell>
                            <Badge className={`text-xs rounded-sm ${
                              c.status === 'graded' ? 'bg-green-100 text-green-700' :
                              c.status === 'exam_scheduled' ? 'bg-purple-100 text-purple-700' :
                              'bg-amber-100 text-amber-700'
                            }`}>
                              {c.status === 'exam_scheduled' ? 'Exam Scheduled' : c.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                className="h-7 px-2 text-xs rounded-sm bg-[#C5A572] text-white"
                                onClick={() => navigate(`/examiner/assess/${c.user_id}`)}
                                data-testid={`assess-btn-${c.id}`}
                              >
                                <FileText className="h-3 w-3 mr-1" /> Full Assessment
                              </Button>
                              {(c.status === 'approved' || c.status === 'exam_scheduled') && (
                                <Button
                                  size="sm"
                                  className="h-7 px-2 text-xs rounded-sm bg-[#1B2A4A]"
                                  onClick={() => setSelectedCandidate(c)}
                                  data-testid={`grade-btn-${c.id}`}
                                >
                                  <ClipboardCheck className="h-3 w-3 mr-1" /> Grade
                                </Button>
                              )}
                              {c.status === 'graded' && c.osce_grade && (
                                <Badge className="bg-green-600 text-white">{c.osce_grade.grade}</Badge>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Grade Modal */}
              {selectedCandidate && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedCandidate(null)}>
                  <Card className="max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">Grade Candidate</h3>
                      <p className="text-sm text-slate-600 mb-4"><strong>{selectedCandidate.full_name}</strong> - {selectedCandidate.examination}</p>
                      
                      <div className="space-y-4">
                        <div>
                          <Label className="text-sm">Grade</Label>
                          <Select value={gradeForm.grade} onValueChange={v => setGradeForm({...gradeForm, grade: v})}>
                            <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="Select grade" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Pass - Distinction">Pass - Distinction</SelectItem>
                              <SelectItem value="Pass - Merit">Pass - Merit</SelectItem>
                              <SelectItem value="Pass">Pass</SelectItem>
                              <SelectItem value="Borderline Pass">Borderline Pass</SelectItem>
                              <SelectItem value="Fail">Fail</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-sm">Feedback (Optional)</Label>
                          <Textarea
                            value={gradeForm.feedback}
                            onChange={e => setGradeForm({...gradeForm, feedback: e.target.value})}
                            placeholder="Performance feedback..."
                            className="rounded-sm mt-1"
                            rows={3}
                          />
                        </div>
                      </div>

                      <div className="flex gap-2 mt-6">
                        <Button onClick={() => handleGrade(selectedCandidate.id)} className="bg-[#1B2A4A] text-white rounded-sm">
                          Submit Grade
                        </Button>
                        <Button variant="outline" onClick={() => setSelectedCandidate(null)} className="rounded-sm">Cancel</Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>

            {/* Case Logs Tab */}
            <TabsContent value="caselogs" className="p-4">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-[#1B2A4A] mb-3 flex items-center gap-2">
                  <ClipboardCheck className="h-5 w-5 text-[#C5A572]" />
                  Submitted Logbooks for Review
                  <Badge className="bg-blue-100 text-blue-700">{submittedLogbooks.length}</Badge>
                </h3>
                
                {submittedLogbooks.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50 rounded-lg">
                    <ClipboardCheck className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No logbooks assigned to you for review yet.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-blue-50">
                          <TableHead className="text-xs font-bold">Candidate</TableHead>
                          <TableHead className="text-xs font-bold">Total Cases</TableHead>
                          <TableHead className="text-xs font-bold">Submitted</TableHead>
                          <TableHead className="text-xs font-bold">Status</TableHead>
                          <TableHead className="text-xs font-bold">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {submittedLogbooks.map((log) => (
                          <TableRow key={log.id} className="bg-blue-50/50">
                            <TableCell>
                              <p className="text-sm font-medium text-[#1B2A4A]">{log.candidate_name}</p>
                              <p className="text-xs text-slate-500">{log.candidate_email}</p>
                            </TableCell>
                            <TableCell>
                              <Badge className="bg-slate-100 text-slate-700">{log.total_cases} cases</Badge>
                            </TableCell>
                            <TableCell className="text-xs text-slate-500">
                              {log.submitted_at ? new Date(log.submitted_at).toLocaleDateString() : 'N/A'}
                            </TableCell>
                            <TableCell>
                              <Badge className={`text-xs rounded-sm ${
                                log.status === 'scored' ? 'bg-green-100 text-green-700' : 
                                log.status === 'assigned' ? 'bg-amber-100 text-amber-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {log.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-xs rounded-sm"
                                  onClick={async () => {
                                    try {
                                      const res = await axios.get(`${API}/examiner/logbook/${log.id}`, {
                                        headers: { Authorization: `Bearer ${token}` }
                                      });
                                      setSelectedLogbook(res.data);
                                    } catch (err) { toast.error('Failed to load logbook'); }
                                  }}
                                >
                                  <Eye className="h-3 w-3 mr-1" /> View
                                </Button>
                                {log.status === 'assigned' && (
                                  <Button
                                    size="sm"
                                    className="h-7 px-2 text-xs rounded-sm bg-[#1B2A4A]"
                                    onClick={() => setSelectedCaseLog(log)}
                                  >
                                    <Award className="h-3 w-3 mr-1" /> Score
                                  </Button>
                                )}
                                {log.scoring && (
                                  <span className="text-sm font-semibold text-green-600">{log.scoring.overall_score}/10</span>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>

              {/* Individual Case Logs Section */}
              <h3 className="text-lg font-semibold text-[#1B2A4A] mb-3 flex items-center gap-2">
                <FileText className="h-5 w-5 text-slate-500" />
                Individual Case Logs
                <Badge className="bg-slate-100 text-slate-700">{caseLogs.length}</Badge>
              </h3>
              
              {caseLogs.length === 0 ? (
                <p className="text-sm text-slate-500 py-8 text-center">No individual case logs assigned to you yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F8FAFC]">
                        <TableHead className="text-xs font-bold">Candidate</TableHead>
                        <TableHead className="text-xs font-bold">Patient</TableHead>
                        <TableHead className="text-xs font-bold">Procedure</TableHead>
                        <TableHead className="text-xs font-bold">Date</TableHead>
                        <TableHead className="text-xs font-bold">Status</TableHead>
                        <TableHead className="text-xs font-bold">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {caseLogs.filter(log => !log.entries_snapshot).map((log) => (
                        <TableRow key={log.id}>
                          <TableCell>
                            <p className="text-sm font-medium text-[#1B2A4A]">{log.candidate_name}</p>
                            <p className="text-xs text-slate-500">{log.candidate_email}</p>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm">{log.patient_name}</p>
                            <p className="text-xs text-slate-500">{log.patient_age}y, {log.patient_gender}</p>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs rounded-sm">{log.procedure_type}</Badge>
                          </TableCell>
                          <TableCell className="text-xs text-slate-500">{new Date(log.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <Badge className={`text-xs rounded-sm ${log.status === 'scored' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                              {log.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {log.status === 'assigned' ? (
                              <Button
                                size="sm"
                                className="h-7 px-2 text-xs rounded-sm bg-[#1B2A4A]"
                                onClick={() => setSelectedCaseLog(log)}
                              >
                                <Award className="h-3 w-3 mr-1" /> Score
                              </Button>
                            ) : log.scoring ? (
                              <span className="text-sm font-semibold text-green-600">{log.scoring.overall_score}/10</span>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Logbook Detail Modal */}
              {selectedLogbook && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedLogbook(null)}>
                  <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-[#1B2A4A]">Logbook Review</h3>
                          <p className="text-sm text-slate-500">{selectedLogbook.candidate_name} - {selectedLogbook.candidate_email}</p>
                        </div>
                        <Badge className={`${
                          selectedLogbook.status === 'scored' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {selectedLogbook.status}
                        </Badge>
                      </div>
                      
                      <div className="bg-slate-50 p-3 rounded-lg mb-4">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div><span className="text-slate-500">Total Cases:</span> <strong>{selectedLogbook.total_cases}</strong></div>
                          <div><span className="text-slate-500">Submitted:</span> {selectedLogbook.submitted_at ? new Date(selectedLogbook.submitted_at).toLocaleDateString() : 'N/A'}</div>
                        </div>
                      </div>

                      <h4 className="font-semibold text-[#1B2A4A] mb-3">Case Entries</h4>
                      <div className="overflow-x-auto max-h-[400px]">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-50 sticky top-0">
                              <TableHead className="text-xs font-bold">Case ID</TableHead>
                              <TableHead className="text-xs font-bold">Procedure</TableHead>
                              <TableHead className="text-xs font-bold">Patient</TableHead>
                              <TableHead className="text-xs font-bold">Diagnosis</TableHead>
                              <TableHead className="text-xs font-bold">Outcome</TableHead>
                              <TableHead className="text-xs font-bold">Before / After</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {selectedLogbook.entries_snapshot?.map((entry, idx) => (
                              <TableRow key={entry.id || idx}>
                                <TableCell className="text-xs font-mono">{entry.case_id}</TableCell>
                                <TableCell className="text-sm">{entry.procedure_type || entry.procedure}</TableCell>
                                <TableCell className="text-sm">{entry.patient_name} ({entry.age}y)</TableCell>
                                <TableCell className="text-xs text-slate-600">{entry.diagnosis}</TableCell>
                                <TableCell>
                                  <Badge className={`text-xs ${entry.complication ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                    {entry.outcome}
                                  </Badge>
                                </TableCell>
                                <TableCell data-testid={`case-images-${entry.case_id}`}>
                                  {(entry.before_image || entry.after_image) ? (
                                    <div className="flex gap-2">
                                      {entry.before_image ? (
                                        <a href={`${process.env.REACT_APP_BACKEND_URL}${entry.before_image}`} target="_blank" rel="noopener noreferrer" className="block text-center" title="View before image">
                                          <img src={`${process.env.REACT_APP_BACKEND_URL}${entry.before_image}`} alt="Before" className="w-14 h-14 object-cover rounded-sm border border-slate-200 hover:border-[#C5A572] transition-colors" />
                                          <span className="text-[9px] text-slate-400 uppercase tracking-wide">Before</span>
                                        </a>
                                      ) : (
                                        <div className="w-14 h-14 flex items-center justify-center bg-slate-50 rounded-sm border border-dashed border-slate-200 text-[9px] text-slate-300">No before</div>
                                      )}
                                      {entry.after_image ? (
                                        <a href={`${process.env.REACT_APP_BACKEND_URL}${entry.after_image}`} target="_blank" rel="noopener noreferrer" className="block text-center" title="View after image">
                                          <img src={`${process.env.REACT_APP_BACKEND_URL}${entry.after_image}`} alt="After" className="w-14 h-14 object-cover rounded-sm border border-slate-200 hover:border-[#C5A572] transition-colors" />
                                          <span className="text-[9px] text-slate-400 uppercase tracking-wide">After</span>
                                        </a>
                                      ) : (
                                        <div className="w-14 h-14 flex items-center justify-center bg-slate-50 rounded-sm border border-dashed border-slate-200 text-[9px] text-slate-300">No after</div>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-xs text-slate-300">—</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                      
                      <div className="flex gap-2 justify-end mt-4 pt-4 border-t">
                        {selectedLogbook.status === 'assigned' && (
                          <Button 
                            onClick={() => { setSelectedCaseLog(selectedLogbook); setSelectedLogbook(null); }}
                            className="bg-[#C5A572] hover:bg-[#B09260] text-white rounded-sm"
                          >
                            <Award className="h-4 w-4 mr-1" /> Score This Logbook
                          </Button>
                        )}
                        <Button variant="outline" onClick={() => setSelectedLogbook(null)} className="rounded-sm">Close</Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Score Case Log Modal */}
              {selectedCaseLog && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedCaseLog(null)}>
                  <Card className="max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-[#1B2A4A] mb-2">
                        {selectedCaseLog.entries_snapshot ? 'Score Logbook Submission' : 'Score Case Log'}
                      </h3>
                      <div className="bg-slate-50 p-3 rounded-sm mb-4">
                        <p className="text-sm"><strong>Candidate:</strong> {selectedCaseLog.candidate_name}</p>
                        {selectedCaseLog.entries_snapshot ? (
                          <p className="text-sm"><strong>Total Cases:</strong> {selectedCaseLog.total_cases}</p>
                        ) : (
                          <>
                            <p className="text-sm"><strong>Patient:</strong> {selectedCaseLog.patient_name} ({selectedCaseLog.patient_age}y, {selectedCaseLog.patient_gender})</p>
                            <p className="text-sm"><strong>Procedure:</strong> {selectedCaseLog.procedure_type}</p>
                            <p className="text-sm"><strong>Diagnosis:</strong> {selectedCaseLog.diagnosis}</p>
                            <p className="text-sm"><strong>Treatment:</strong> {selectedCaseLog.treatment_plan}</p>
                            <p className="text-sm"><strong>Outcome:</strong> {selectedCaseLog.outcome}</p>
                          </>
                        )}
                      </div>
                      
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Clinical Reasoning (1-10)</Label>
                            <Input
                              type="number"
                              min={1}
                              max={10}
                              value={caseScoreForm.clinical_reasoning_score}
                              onChange={e => setCaseScoreForm({...caseScoreForm, clinical_reasoning_score: parseInt(e.target.value) || 1})}
                              className="rounded-sm mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Technique (1-10)</Label>
                            <Input
                              type="number"
                              min={1}
                              max={10}
                              value={caseScoreForm.technique_score}
                              onChange={e => setCaseScoreForm({...caseScoreForm, technique_score: parseInt(e.target.value) || 1})}
                              className="rounded-sm mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Documentation (1-10)</Label>
                            <Input
                              type="number"
                              min={1}
                              max={10}
                              value={caseScoreForm.documentation_score}
                              onChange={e => setCaseScoreForm({...caseScoreForm, documentation_score: parseInt(e.target.value) || 1})}
                              className="rounded-sm mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Patient Safety (1-10)</Label>
                            <Input
                              type="number"
                              min={1}
                              max={10}
                              value={caseScoreForm.patient_safety_score}
                              onChange={e => setCaseScoreForm({...caseScoreForm, patient_safety_score: parseInt(e.target.value) || 1})}
                              className="rounded-sm mt-1"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs">Overall Score (1-10)</Label>
                          <Input
                            type="number"
                            min={1}
                            max={10}
                            value={caseScoreForm.overall_score}
                            onChange={e => setCaseScoreForm({...caseScoreForm, overall_score: parseInt(e.target.value) || 1})}
                            className="rounded-sm mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Feedback to Candidate</Label>
                          <Textarea
                            value={caseScoreForm.feedback}
                            onChange={e => setCaseScoreForm({...caseScoreForm, feedback: e.target.value})}
                            placeholder="Constructive feedback for the candidate..."
                            className="rounded-sm mt-1"
                            rows={2}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Examiner Notes (Internal)</Label>
                          <Textarea
                            value={caseScoreForm.examiner_notes}
                            onChange={e => setCaseScoreForm({...caseScoreForm, examiner_notes: e.target.value})}
                            placeholder="Internal notes..."
                            className="rounded-sm mt-1"
                            rows={2}
                          />
                        </div>
                      </div>

                      <div className="flex gap-2 mt-6">
                        <Button onClick={handleScoreCaseLog} className="bg-[#1B2A4A] text-white rounded-sm">
                          Submit Score
                        </Button>
                        <Button variant="outline" onClick={() => setSelectedCaseLog(null)} className="rounded-sm">Cancel</Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>


            {/* Viva Schedules Tab */}
            <TabsContent value="viva" data-testid="viva-tab-content">
              <VivaTab 
                token={token}
                allocatedCandidates={allocatedCandidates}
                vivaSchedules={vivaSchedules}
                fetchVivaSchedules={fetchVivaSchedules}
              />
            </TabsContent>

            {/* OSCE Schedules Tab */}
            <TabsContent value="schedules" data-testid="osce-tab-content">
              <OsceTab
                token={token}
                allocatedCandidates={allocatedCandidates}
                schedules={schedules}
                fetchSchedules={fetchSchedules}
              />
            </TabsContent>

            {/* Question Bank Tab */}
            <TabsContent value="questions" data-testid="questions-tab-content">
              <QuestionBankTab
                token={token}
                questionBank={questionBank}
                fetchQuestionBank={fetchQuestionBank}
              />
            </TabsContent>
            {/* Earnings Tab */}
            <TabsContent value="earnings" className="p-4" data-testid="earnings-tab-content">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-[#1B2A4A]">Examiner Earnings Dashboard</h3>
                <p className="text-sm text-slate-500">Track your revenue share from completed assessments (10% share)</p>
              </div>

              {/* Earnings Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
                  <CardContent className="p-4">
                    <IndianRupee className="h-8 w-8 mb-2 opacity-80" />
                    <p className="text-2xl font-bold">₹{earnings?.total_earnings?.toLocaleString() || 0}</p>
                    <p className="text-xs opacity-80">Total Earnings</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                  <CardContent className="p-4">
                    <CheckCircle className="h-8 w-8 mb-2 opacity-80" />
                    <p className="text-2xl font-bold">{earnings?.total_assessments || 0}</p>
                    <p className="text-xs opacity-80">Completed Assessments</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                  <CardContent className="p-4">
                    <Clock className="h-8 w-8 mb-2 opacity-80" />
                    <p className="text-2xl font-bold">{earnings?.pending_assessments || 0}</p>
                    <p className="text-xs opacity-80">Pending Review</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                  <CardContent className="p-4">
                    <TrendingUp className="h-8 w-8 mb-2 opacity-80" />
                    <p className="text-2xl font-bold">₹{earnings?.pending_earnings_estimate?.toLocaleString() || 0}</p>
                    <p className="text-xs opacity-80">Pending Earnings Est.</p>
                  </CardContent>
                </Card>
              </div>

              {/* Monthly Breakdown */}
              <Card className="imcac-card mb-6">
                <CardHeader>
                  <CardTitle className="text-base">Monthly Earnings Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  {earnings?.monthly_breakdown?.length > 0 ? (
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={earnings.monthly_breakdown}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, 'Earnings']} />
                          <Bar dataKey="earnings" fill="#1B2A4A" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <p className="text-center py-8 text-slate-500">No earnings data available yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Revenue Share Info */}
              <Card className="border-l-4 border-l-[#C5A572]">
                <CardContent className="p-4">
                  <h4 className="font-semibold text-[#1B2A4A] mb-2">Revenue Share Structure</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Examiner Share</p>
                      <p className="text-xl font-bold text-emerald-600">10%</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Exam Center Share</p>
                      <p className="text-xl font-bold text-blue-600">30%</p>
                    </div>
                    <div>
                      <p className="text-slate-500">IMCAC Share</p>
                      <p className="text-xl font-bold text-[#1B2A4A]">60%</p>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-3">{earnings?.payment_status || 'Payments are processed monthly. Contact admin for details.'}</p>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Requests Tab */}
            <TabsContent value="requests" className="p-4" data-testid="requests-tab-content">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-[#1B2A4A]">Reschedule & Reassignment Requests</h3>
                  <p className="text-sm text-slate-500">Request changes to your scheduled examinations</p>
                </div>
                <Button onClick={() => setShowRescheduleForm(true)} className="bg-[#1B2A4A] text-white rounded-sm">
                  <Plus className="h-4 w-4 mr-1" /> New Request
                </Button>
              </div>

              {rescheduleRequests.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CalendarClock className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No reschedule requests made yet.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {rescheduleRequests.map((req) => (
                    <Card key={req.id}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className="bg-slate-100">{req.request_type?.replace(/_/g, ' ')}</Badge>
                              <Badge className={`text-xs ${
                                req.status === 'approved' ? 'bg-green-100 text-green-700' :
                                req.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                'bg-amber-100 text-amber-700'
                              }`}>
                                {req.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-[#1B2A4A]">{req.reason}</p>
                            <p className="text-xs text-slate-500 mt-1">
                              Submitted: {new Date(req.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          {req.status === 'approved' && req.approved_date && (
                            <div className="text-right">
                              <p className="text-xs text-slate-500">New Date</p>
                              <p className="text-sm font-medium text-green-600">{req.approved_date}</p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Reschedule Request Modal */}
              {showRescheduleForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowRescheduleForm(false)}>
                  <Card className="max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">Submit Reschedule Request</h3>
                      
                      <div className="space-y-4">
                        <div>
                          <Label className="text-sm">Request Type *</Label>
                          <Select value={rescheduleForm.request_type} onValueChange={v => setRescheduleForm({...rescheduleForm, request_type: v})}>
                            <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="Select type" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="viva_reschedule">Viva Reschedule</SelectItem>
                              <SelectItem value="osce_reschedule">OSCE Reschedule</SelectItem>
                              <SelectItem value="reassignment">Candidate Reassignment</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div>
                          <Label className="text-sm">Schedule ID *</Label>
                          <Input
                            value={rescheduleForm.schedule_id}
                            onChange={e => setRescheduleForm({...rescheduleForm, schedule_id: e.target.value})}
                            placeholder="Enter schedule ID"
                            className="rounded-sm mt-1"
                          />
                        </div>
                        
                        <div>
                          <Label className="text-sm">Reason *</Label>
                          <Textarea
                            value={rescheduleForm.reason}
                            onChange={e => setRescheduleForm({...rescheduleForm, reason: e.target.value})}
                            placeholder="Explain the reason for your request..."
                            className="rounded-sm mt-1"
                            rows={3}
                          />
                        </div>
                        
                        <div>
                          <Label className="text-sm">Additional Notes</Label>
                          <Textarea
                            value={rescheduleForm.additional_notes}
                            onChange={e => setRescheduleForm({...rescheduleForm, additional_notes: e.target.value})}
                            placeholder="Any additional information..."
                            className="rounded-sm mt-1"
                            rows={2}
                          />
                        </div>
                      </div>

                      <div className="flex gap-2 mt-6">
                        <Button onClick={handleSubmitRescheduleRequest} className="bg-[#1B2A4A] text-white rounded-sm">
                          Submit Request
                        </Button>
                        <Button variant="outline" onClick={() => setShowRescheduleForm(false)} className="rounded-sm">Cancel</Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>

            {/* Transcripts Tab */}
            <TabsContent value="transcripts" className="p-4" data-testid="transcripts-tab-content">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-[#1B2A4A] flex items-center gap-2">
                    <FileText className="h-5 w-5 text-amber-600" />
                    Submit Transcripts for Admin Verification
                  </h3>
                </div>

                <p className="text-sm text-slate-500 bg-blue-50 p-3 rounded-sm">
                  Select a candidate who has completed their examination to submit their transcript scores. 
                  The transcript will be sent to admin for verification before being officially issued.
                </p>

                {/* Candidate Selection */}
                <div className="grid md:grid-cols-2 gap-4">
                  <Card className="imcac-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Select Candidate</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {transcriptCandidates.length === 0 ? (
                        <p className="text-sm text-slate-500 py-4 text-center">No candidates ready for transcript submission.</p>
                      ) : (
                        <div className="max-h-64 overflow-y-auto space-y-2">
                          {transcriptCandidates.map(c => (
                            <div 
                              key={c.id}
                              className={`p-3 border rounded-sm cursor-pointer transition-all ${selectedTranscriptCandidate?.id === c.id ? 'border-amber-500 bg-amber-50' : 'hover:border-slate-400'}`}
                              onClick={() => setSelectedTranscriptCandidate(c)}
                            >
                              <p className="text-sm font-medium text-[#1B2A4A]">{c.full_name}</p>
                              <p className="text-xs text-slate-500">{c.email}</p>
                              <div className="flex gap-2 mt-1">
                                <Badge variant="outline" className="text-xs">{c.examination}</Badge>
                                <Badge className={`text-xs ${c.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                  {c.status}
                                </Badge>
                                {c.transcript_status && (
                                  <Badge className="text-xs bg-amber-100 text-amber-700">{c.transcript_status}</Badge>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Score Entry Form */}
                  {selectedTranscriptCandidate && (
                    <Card className="imcac-card border-2 border-amber-400">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Enter Scores for {selectedTranscriptCandidate.full_name}</CardTitle>
                        <CardDescription className="text-xs">All scores should be out of 100</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Theory/Written Score</Label>
                            <Input 
                              type="number" 
                              min="0" 
                              max="100"
                              value={transcriptForm.subject_scores.theory || ''} 
                              onChange={e => setTranscriptForm({...transcriptForm, subject_scores: {...transcriptForm.subject_scores, theory: parseInt(e.target.value) || 0}})}
                              className="rounded-sm mt-1 text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Practical Score</Label>
                            <Input 
                              type="number" 
                              min="0" 
                              max="100"
                              value={transcriptForm.practical_score || ''} 
                              onChange={e => setTranscriptForm({...transcriptForm, practical_score: parseInt(e.target.value) || 0})}
                              className="rounded-sm mt-1 text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">OSCE Score</Label>
                            <Input 
                              type="number" 
                              min="0" 
                              max="100"
                              value={transcriptForm.osce_score || ''} 
                              onChange={e => setTranscriptForm({...transcriptForm, osce_score: parseInt(e.target.value) || 0})}
                              className="rounded-sm mt-1 text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Viva Score</Label>
                            <Input 
                              type="number" 
                              min="0" 
                              max="100"
                              value={transcriptForm.viva_score || ''} 
                              onChange={e => setTranscriptForm({...transcriptForm, viva_score: parseInt(e.target.value) || 0})}
                              className="rounded-sm mt-1 text-sm"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs">MCQ Score</Label>
                          <Input 
                            type="number" 
                            min="0" 
                            max="100"
                            value={transcriptForm.subject_scores.mcq || ''} 
                            onChange={e => setTranscriptForm({...transcriptForm, subject_scores: {...transcriptForm.subject_scores, mcq: parseInt(e.target.value) || 0}})}
                            className="rounded-sm mt-1 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Overall Remarks</Label>
                          <Textarea 
                            value={transcriptForm.overall_remarks} 
                            onChange={e => setTranscriptForm({...transcriptForm, overall_remarks: e.target.value})}
                            placeholder="Enter any remarks or observations about the candidate's performance..."
                            className="rounded-sm mt-1 text-sm"
                            rows={3}
                          />
                        </div>
                        <div className="flex gap-2 pt-2">
                          <Button 
                            onClick={handleSubmitTranscript}
                            className="bg-[#C5A572] hover:bg-[#b8956a] text-white rounded-sm text-sm"
                          >
                            <CheckCircle className="h-4 w-4 mr-1" /> Submit to Admin
                          </Button>
                          <Button 
                            variant="outline"
                            onClick={() => {
                              setSelectedTranscriptCandidate(null);
                              setTranscriptForm({ subject_scores: {}, practical_score: 0, osce_score: 0, viva_score: 0, overall_remarks: '', documents: [] });
                            }}
                            className="rounded-sm text-sm"
                          >
                            Cancel
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Submitted Transcripts */}
                <div className="mt-6">
                  <h4 className="text-sm font-semibold text-[#1B2A4A] mb-3 flex items-center gap-2">
                    <ClipboardCheck className="h-4 w-4 text-purple-600" />
                    My Submitted Transcripts ({submittedTranscripts.length})
                  </h4>
                  {submittedTranscripts.length === 0 ? (
                    <p className="text-sm text-slate-500 bg-slate-50 p-4 rounded-sm text-center">No transcripts submitted yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-[#F8FAFC]">
                            <TableHead className="text-xs font-bold">Candidate</TableHead>
                            <TableHead className="text-xs font-bold">Scores</TableHead>
                            <TableHead className="text-xs font-bold">Status</TableHead>
                            <TableHead className="text-xs font-bold">Submitted</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {submittedTranscripts.map(t => (
                            <TableRow key={t.id}>
                              <TableCell>
                                <p className="text-sm font-medium text-[#1B2A4A]">{t.candidate_name}</p>
                                <p className="text-xs text-slate-500">{t.candidate_email}</p>
                              </TableCell>
                              <TableCell>
                                <div className="text-xs">
                                  <p>Theory: {t.subject_scores?.theory || '-'}</p>
                                  <p>Practical: {t.practical_score || '-'}</p>
                                  <p>Total: {t.percentage?.toFixed(1) || '-'}%</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge className={`text-xs rounded-sm ${
                                  t.status === 'issued' ? 'bg-purple-100 text-purple-700' :
                                  t.status === 'approved' ? 'bg-green-100 text-green-700' :
                                  t.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                  'bg-amber-100 text-amber-700'
                                }`}>
                                  {t.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-xs text-slate-500">
                                {t.submitted_at ? new Date(t.submitted_at).toLocaleDateString() : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Messages Tab */}
            <TabsContent value="messages" className="p-4" data-testid="messages-tab-content">
              <MessagesTab token={token} portalType="examiner" portalName="Examiner" />
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
}
